import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarRange, GripVertical, Plus, Trash2, Pencil, Check, X, Sparkles, Map as MapIcon, List, Umbrella, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { WxIcon } from "../Weather";

/* Day-by-day itinerary board — drag & drop activities between days, inline edit,
   add/remove days. Stored on the trip document as `day_plan`. */
export default function DayPlanBoard({ trip, readOnly, onChange }) {
  const [plan, setPlan] = useState(trip.day_plan || []);
  const [editing, setEditing] = useState(null); // "di-ai" key
  const [adding, setAdding] = useState(null); // day index
  const [drag, setDrag] = useState(null); // {di, ai}
  const [view, setView] = useState("board"); // board | map
  const [wx, setWx] = useState(null); // date -> {code, hi, lo, rain_pct}
  const [sharing, setSharing] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  /* Pending friend suggestions (owner/edit only) — accept adds to the day with one tap. */
  const loadSuggestions = () => {
    if (readOnly) return;
    api.get(`/trips/${trip.id}/suggestions`).then((r) => setSuggestions(r.data.items || [])).catch(() => {});
  };
  useEffect(loadSuggestions, [trip.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const acceptSuggestion = async (s) => {
    try {
      const { data } = await api.post(`/trips/${trip.id}/suggestions/${s.id}/accept`);
      setPlan(data.day_plan || []);
      setSuggestions((l) => l.filter((x) => x.id !== s.id));
      toast.success(`Added to Day ${s.day}`);
      onChange?.();
    } catch { toast.error("Could not accept the suggestion"); }
  };
  const dismissSuggestion = async (s) => {
    try {
      await api.post(`/trips/${trip.id}/suggestions/${s.id}/dismiss`);
      setSuggestions((l) => l.filter((x) => x.id !== s.id));
    } catch { toast.error("Could not dismiss"); }
  };

  // Resolve each day's calendar date: explicit date, else trip start_date + index.
  const dayDate = (d, i) => {
    if (d.date) return d.date;
    if (!trip.start_date) return null;
    const dt = new Date(`${trip.start_date}T12:00:00`);
    dt.setDate(dt.getDate() + i);
    return dt.toISOString().slice(0, 10);
  };

  /* Live per-day forecast so travelers can reshuffle activities before rain hits. */
  useEffect(() => {
    if (!plan.length || !(trip.stops || []).length) return;
    if (!plan.some((d, i) => dayDate(d, i))) return;
    api.get("/weather/trip", { params: { places: trip.stops.join(","), start: trip.start_date || undefined, end: trip.end_date || undefined } })
      .then((r) => {
        const good = (r.data.stops || []).find((s) => !s.error && s.days?.length);
        if (!good) return;
        const map = {};
        good.days.forEach((d) => { map[d.date] = d; });
        setWx(map);
      }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trip.id]);

  /* Share My Plan — read-only link (day plan included, documents/PNRs never) via WhatsApp. */
  const sharePlan = async () => {
    setSharing(true);
    try {
      const { data } = await api.post(`/trips/${trip.id}/share-link`, {});
      const link = `${window.location.origin}${data.path}`;
      const msg = `Check out our trip plan: ${trip.title}${trip.start_date ? ` (${trip.start_date}${trip.end_date ? ` – ${trip.end_date}` : ""})` : ""} — day-by-day itinerary here:\n${link}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
      toast.success("Read-only plan link created — pick a chat in WhatsApp");
    } catch { toast.error("Could not create the share link"); }
    finally { setSharing(false); }
  };

  const persist = async (next) => {
    setPlan(next);
    try {
      await api.post("/trips", { id: trip.id, title: trip.title, day_plan: next });
      onChange?.();
    } catch { toast.error("Could not save the itinerary"); }
  };

  const addDay = () => {
    const next = [...plan, { day: plan.length + 1, date: null, title: `Day ${plan.length + 1}`, activities: [] }];
    persist(next);
  };
  const removeDay = (di) => {
    const next = plan.filter((_, i) => i !== di).map((d, i) => ({ ...d, day: i + 1 }));
    persist(next);
  };
  const setDayTitle = (di, title) => {
    const next = plan.map((d, i) => (i === di ? { ...d, title } : d));
    persist(next);
  };
  const removeActivity = (di, ai) => {
    const next = plan.map((d, i) => (i === di ? { ...d, activities: d.activities.filter((_, j) => j !== ai) } : d));
    persist(next);
  };
  const saveActivity = (di, ai, a) => {
    const next = plan.map((d, i) => {
      if (i !== di) return d;
      const acts = [...(d.activities || [])];
      if (ai == null) acts.push(a); else acts[ai] = a;
      return { ...d, activities: acts };
    });
    setEditing(null); setAdding(null);
    persist(next);
  };
  const dropOn = (di, ai) => {
    if (!drag) return;
    const next = plan.map((d) => ({ ...d, activities: [...(d.activities || [])] }));
    const [moved] = next[drag.di].activities.splice(drag.ai, 1);
    const target = ai == null ? next[di].activities.length : ai;
    next[di].activities.splice(target, 0, moved);
    setDrag(null);
    persist(next);
  };

  if (!plan.length && readOnly) return null;

  return (
    <section className="mb-10" data-testid="trip-dayplan">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <h2 className="font-heading font-900 text-emerald-600 text-2xl flex items-center gap-2">
          <CalendarRange className="w-6 h-6" /> Day-by-Day Itinerary
          {trip.planned_by_ai && <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wide bg-emerald-50 border border-emerald-300 text-emerald-700 px-1.5 py-0.5"><Sparkles className="w-3 h-3" /> AI planned</span>}
        </h2>
        <div className="flex items-center gap-2">
          {plan.length > 0 && (!trip.my_role || trip.my_role === "owner") && (
            <button onClick={sharePlan} disabled={sharing} data-testid="dayplan-share-whatsapp"
              title="Send a beautiful read-only itinerary link on WhatsApp — documents & PNRs stay private"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-[#25D366] text-white text-sm font-600 hover:opacity-90 disabled:opacity-60">
              <MessageCircle className="w-4 h-4" /> {sharing ? "Creating link…" : "Share plan"}
            </button>
          )}
          {plan.length > 0 && (
            <div className="inline-flex border border-zinc-300 text-xs font-600">
              <button onClick={() => setView("board")} data-testid="dayplan-view-board"
                className={`px-2.5 py-1.5 inline-flex items-center gap-1 ${view === "board" ? "bg-zinc-800 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}><List className="w-3.5 h-3.5" /> Days</button>
              <button onClick={() => { setView("map"); setTimeout(() => document.getElementById("trip-map-section")?.scrollIntoView({ behavior: "smooth" }), 50); }} data-testid="dayplan-view-map"
                className={`px-2.5 py-1.5 inline-flex items-center gap-1 ${view === "map" ? "bg-zinc-800 text-white" : "text-zinc-600 hover:bg-zinc-100"}`}><MapIcon className="w-3.5 h-3.5" /> Map</button>
            </div>
          )}
          {!readOnly && <button onClick={addDay} data-testid="dayplan-add-day"
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-sm font-600 hover:bg-emerald-700"><Plus className="w-4 h-4" /> Add day</button>}
        </div>
      </div>

      {suggestions.length > 0 && !readOnly && (
        <div className="mb-4 border border-violet-300 bg-violet-50/50 p-3 space-y-2" data-testid="dayplan-suggestions">
          <div className="text-xs font-mono font-bold uppercase tracking-widest text-violet-700">Friend suggestions ({suggestions.length})</div>
          {suggestions.map((s) => (
            <div key={s.id} className="flex flex-wrap items-center gap-2 bg-white border border-violet-200 px-3 py-2" data-testid={`suggestion-${s.id}`}>
              <span className="text-sm text-zinc-800 flex-1 min-w-[180px]">
                <b>{s.from_name}</b> · Day {s.day}{s.time ? ` · ${s.time}` : ""}: {s.text}{s.place ? <span className="text-muted-foreground"> · {s.place}</span> : null}
              </span>
              <button onClick={() => acceptSuggestion(s)} data-testid={`suggestion-accept-${s.id}`}
                className="inline-flex items-center gap-1 bg-emerald-600 text-white px-2.5 py-1.5 text-xs font-700 hover:bg-emerald-700"><Check className="w-3.5 h-3.5" /> Add to plan</button>
              <button onClick={() => dismissSuggestion(s)} data-testid={`suggestion-dismiss-${s.id}`}
                className="inline-flex items-center gap-1 border border-zinc-300 text-zinc-500 px-2.5 py-1.5 text-xs font-600 hover:border-zinc-500"><X className="w-3.5 h-3.5" /> Dismiss</button>
            </div>
          ))}
        </div>
      )}

      {plan.length === 0 ? (
        <div className="border border-dashed border-emerald-300 bg-emerald-50/40 p-5 text-sm text-zinc-700">
          No day-by-day plan yet. <Link to="/plan-ai" className="text-emerald-700 font-700 hover:underline" data-testid="dayplan-ai-link">Let the AI Trip Planner build one</Link>, or add days manually.
        </div>
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plan.map((d, di) => (
            <div key={di} className="border border-zinc-300 bg-white flex flex-col" data-testid={`dayplan-day-${di}`}
              onDragOver={(e) => e.preventDefault()} onDrop={() => dropOn(di, null)}>
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-zinc-100 bg-emerald-50/50">
                <span className="shrink-0 w-8 h-8 bg-white border border-emerald-300 text-emerald-700 font-heading font-900 text-sm flex items-center justify-center">D{d.day}</span>
                {editing === `title-${di}` ? (
                  <TitleEditor initial={d.title} onSave={(t) => { setEditing(null); setDayTitle(di, t); }} onCancel={() => setEditing(null)} />
                ) : (
                  <>
                    <div className="flex-1 min-w-0">
                      <div className="font-700 text-sm truncate">{d.title}</div>
                      {d.date && <div className="text-[11px] text-muted-foreground font-mono">{d.date}</div>}
                    </div>
                    {wx?.[dayDate(d, di)] && (() => {
                      const w = wx[dayDate(d, di)];
                      const rainy = (w.rain_pct || 0) >= 50 || (w.code || 0) >= 61;
                      return (
                        <span data-testid={`dayplan-weather-${di}`}
                          title={rainy ? `Rain risk ${w.rain_pct ?? "?"}% — consider moving outdoor activities` : `Forecast: ${Math.round(w.hi ?? 0)}° / ${Math.round(w.lo ?? 0)}°`}
                          className={`shrink-0 inline-flex items-center gap-1 text-[11px] font-600 px-1.5 py-0.5 border ${rainy ? "bg-amber-50 border-amber-300 text-amber-800" : "bg-sky-50 border-sky-200 text-sky-800"}`}>
                          {rainy ? <Umbrella className="w-3 h-3" /> : <WxIcon code={w.code} className="w-3 h-3" />}
                          {w.hi != null && `${Math.round(w.hi)}°`}{w.rain_pct != null && rainy ? ` ${w.rain_pct}%` : ""}
                        </span>
                      );
                    })()}
                    {!readOnly && (
                      <>
                        <button onClick={() => setEditing(`title-${di}`)} data-testid={`dayplan-edit-title-${di}`} className="text-zinc-400 hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => removeDay(di)} data-testid={`dayplan-remove-day-${di}`} className="text-zinc-400 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="flex-1 p-2 space-y-1.5">
                {(d.activities || []).map((a, ai) => (
                  editing === `${di}-${ai}` ? (
                    <ActivityEditor key={ai} initial={a} onSave={(v) => saveActivity(di, ai, v)} onCancel={() => setEditing(null)} />
                  ) : (
                    <div key={ai} draggable={!readOnly} data-testid={`dayplan-activity-${di}-${ai}`}
                      onDragStart={() => setDrag({ di, ai })}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDrop={(e) => { e.stopPropagation(); dropOn(di, ai); }}
                      className={`group flex items-start gap-1.5 border border-zinc-200 bg-white px-2 py-1.5 text-sm ${readOnly ? "" : "cursor-grab active:cursor-grabbing hover:border-emerald-400"}`}>
                      {!readOnly && <GripVertical className="w-3.5 h-3.5 text-zinc-300 mt-0.5 shrink-0" />}
                      {a.time && <span className="shrink-0 font-mono text-[11px] text-emerald-700 bg-emerald-50 px-1 py-0.5 mt-0.5">{a.time}</span>}
                      <span className="flex-1 min-w-0 text-zinc-700">{a.text}{a.place ? <span className="text-muted-foreground"> · {a.place}</span> : null}</span>
                      {!readOnly && (
                        <span className="shrink-0 flex gap-1 opacity-0 group-hover:opacity-100">
                          <button onClick={() => setEditing(`${di}-${ai}`)} className="text-zinc-400 hover:text-primary"><Pencil className="w-3.5 h-3.5" /></button>
                          <button onClick={() => removeActivity(di, ai)} className="text-zinc-400 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                        </span>
                      )}
                    </div>
                  )
                ))}
                {adding === di ? (
                  <ActivityEditor initial={{ time: "", text: "", place: "" }} onSave={(v) => saveActivity(di, null, v)} onCancel={() => setAdding(null)} />
                ) : (!readOnly && (
                  <button onClick={() => setAdding(di)} data-testid={`dayplan-add-activity-${di}`}
                    className="w-full border border-dashed border-zinc-300 text-zinc-500 text-xs py-1.5 hover:border-emerald-400 hover:text-emerald-700 inline-flex items-center justify-center gap-1">
                    <Plus className="w-3.5 h-3.5" /> Add activity
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      {!readOnly && plan.length > 0 && (
        <p className="mt-2 text-xs text-muted-foreground">Drag activities between days to reorder. Changes save instantly.</p>
      )}
    </section>
  );
}

function TitleEditor({ initial, onSave, onCancel }) {
  const [v, setV] = useState(initial);
  return (
    <div className="flex-1 flex items-center gap-1">
      <input value={v} onChange={(e) => setV(e.target.value)} autoFocus data-testid="dayplan-title-input"
        className="flex-1 min-w-0 border border-zinc-300 px-2 py-1 text-sm outline-none focus:ring-1 focus:ring-emerald-500" />
      <button onClick={() => v.trim() && onSave(v.trim())} className="text-emerald-600"><Check className="w-4 h-4" /></button>
      <button onClick={onCancel} className="text-zinc-400"><X className="w-4 h-4" /></button>
    </div>
  );
}

function ActivityEditor({ initial, onSave, onCancel }) {
  const [time, setTime] = useState(initial.time || "");
  const [text, setText] = useState(initial.text || "");
  const [place, setPlace] = useState(initial.place || "");
  return (
    <div className="border border-emerald-300 bg-emerald-50/40 p-2 space-y-1.5" data-testid="dayplan-activity-editor">
      <div className="flex gap-1.5">
        <input value={time} onChange={(e) => setTime(e.target.value)} placeholder="09:00" data-testid="dayplan-act-time"
          className="w-16 border border-zinc-300 px-1.5 py-1 text-xs font-mono outline-none focus:ring-1 focus:ring-emerald-500" />
        <input value={place} onChange={(e) => setPlace(e.target.value)} placeholder="Place (optional)" data-testid="dayplan-act-place"
          className="flex-1 border border-zinc-300 px-1.5 py-1 text-xs outline-none focus:ring-1 focus:ring-emerald-500" />
      </div>
      <textarea value={text} onChange={(e) => setText(e.target.value)} placeholder="What's happening?" rows={2} autoFocus data-testid="dayplan-act-text"
        className="w-full border border-zinc-300 px-1.5 py-1 text-sm outline-none focus:ring-1 focus:ring-emerald-500" />
      <div className="flex justify-end gap-1.5">
        <button onClick={onCancel} className="px-2 py-1 text-xs border border-zinc-300">Cancel</button>
        <button onClick={() => text.trim() && onSave({ time: time.trim(), text: text.trim(), place: place.trim() || null })}
          data-testid="dayplan-act-save" className="px-2.5 py-1 text-xs bg-emerald-600 text-white font-600">Save</button>
      </div>
    </div>
  );
}
